import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Phone, Clock, Scissors, ArrowRight, Navigation } from "lucide-react";
import { api, MXN } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { WhatsAppButton, WHATSAPP_NUMBER } from "@/components/WhatsAppButton";

const HERO_IMG =
  "https://images.pexels.com/photos/13068377/pexels-photo-13068377.jpeg";

const ADDRESS = "Avenida 15 de mayo 224, Puebla, Puebla";
const MAP_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`;
const MAP_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ADDRESS)}`;

const GALLERY = [
  { url: "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f", label: "Estilismo" },
  { url: "https://images.pexels.com/photos/8834072/pexels-photo-8834072.jpeg", label: "Corte de dama" },
  { url: "https://images.unsplash.com/photo-1605980766335-d3a41c7332a1", label: "Balayage" },
  { url: "https://images.pexels.com/photos/6135675/pexels-photo-6135675.jpeg", label: "Manicure" },
  { url: "https://images.unsplash.com/photo-1709477542149-f4e0e21d590b", label: "Maquillaje" },
  { url: "https://images.pexels.com/photos/4625626/pexels-photo-4625626.jpeg", label: "Barbería" },
  { url: "https://images.unsplash.com/photo-1554519934-e32b1629d9ee", label: "Color y mechas" },
  { url: "https://images.pexels.com/photos/21316248/pexels-photo-21316248.jpeg", label: "Faciales" },
  { url: "https://images.pexels.com/photos/10593034/pexels-photo-10593034.jpeg", label: "Corte rubio" },
  { url: "https://images.pexels.com/photos/161737/pedicure-massage-therapist-spa-161737.jpeg", label: "Pedicure" },
];

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`mx-auto max-w-7xl px-5 sm:px-8 ${className}`}>
      {children}
    </section>
  );
}

function Overline({ children }) {
  return <p className="overline mb-3 text-primary">{children}</p>;
}

export default function Landing() {
  const [services, setServices] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [activeCat, setActiveCat] = useState("Todos");

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/stylists").then((r) => setStylists(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const categories = ["Todos", ...Array.from(new Set(services.map((s) => s.category)))];
  const filtered = activeCat === "Todos" ? services : services.filter((s) => s.category === activeCat);

  return (
    <div className="App overflow-x-hidden bg-background">
      <Navbar />
      <WhatsAppButton />

      {/* HERO */}
      <section id="inicio" className="relative flex min-h-screen items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Salón Lupita" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 px-5 pt-24 sm:px-8 lg:grid-cols-12">
          <motion.div initial="hidden" animate="show" className="lg:col-span-8">
            <motion.div variants={fade} custom={0}>
              <Overline>Estética Unisex · Desde 2010</Overline>
            </motion.div>
            <motion.h1
              variants={fade}
              custom={1}
              className="font-serif-display text-5xl font-light tracking-tight text-white sm:text-6xl lg:text-7xl"
            >
              Belleza que <span className="text-primary">inspira</span><br />confianza
            </motion.h1>
            <motion.p
              variants={fade}
              custom={2}
              className="mt-6 max-w-xl text-base font-light text-white/80 sm:text-lg"
            >
              Cortes, color, maquillaje y cuidado de uñas con la atención premium que
              mereces. Reserva tu cita en segundos.
            </motion.p>
            <motion.div variants={fade} custom={3} className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/reservar"
                data-testid="hero-book-btn"
                className="group flex items-center gap-2 bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark"
              >
                Reservar ahora
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/40 px-7 py-3.5 text-sm font-medium text-white transition-colors hover:border-primary hover:text-primary"
              >
                Escríbenos
              </a>
            </motion.div>
            <motion.div variants={fade} custom={4} className="mt-12 flex gap-10">
              {[
                { n: "5K+", l: "Clientes felices" },
                { n: "11", l: "Servicios" },
                { n: "4.9", l: "Calificación" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-serif-display text-3xl text-white">{s.n}</p>
                  <p className="text-xs text-white/60">{s.l}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <Section id="servicios" className="py-24">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Overline>Nuestros servicios</Overline>
            <h2 className="font-serif-display text-4xl font-light tracking-tight sm:text-5xl">
              Cuidamos cada detalle
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`service-cat-${c}`}
                onClick={() => setActiveCat(c)}
                className={`border px-4 py-2 text-sm transition-colors ${
                  activeCat === c
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:border-primary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s, i) => (
            <motion.div
              key={s.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              custom={i % 3}
              data-testid={`service-card-${s.id}`}
              className="group relative overflow-hidden bg-card p-6"
            >
              <div className="mb-4 h-44 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="overline text-primary">{s.category}</p>
                  <h3 className="font-serif-display text-2xl">{s.name}</h3>
                </div>
                <span className="whitespace-nowrap font-serif-display text-xl text-primary">
                  {MXN(s.price)}
                </span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{s.description}</p>
              <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock size={13} strokeWidth={1.5} /> {s.duration} min
                </span>
                {s.promo && (
                  <span className="bg-primary/15 px-2 py-0.5 text-primary">{s.promo}</span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* TEAM */}
      <Section className="py-12">
        <Overline>Nuestro equipo</Overline>
        <h2 className="mb-10 font-serif-display text-4xl font-light tracking-tight sm:text-5xl">
          Manos expertas
        </h2>
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {stylists.map((st, i) => (
            <motion.div
              key={st.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              custom={i}
              className="group text-center"
            >
              <div className="mx-auto mb-4 aspect-square w-full overflow-hidden rounded-full border border-border">
                <img
                  src={st.photo}
                  alt={st.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="font-serif-display text-xl">{st.name}</h3>
              <p className="text-sm text-primary">{st.role}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* GALLERY */}
      <Section id="galeria" className="py-24">
        <Overline>Galería</Overline>
        <h2 className="mb-10 font-serif-display text-4xl font-light tracking-tight sm:text-5xl">
          Nuestros trabajos anteriores
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {GALLERY.map((g, i) => (
            <motion.div
              key={g.url}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              custom={i % 6}
              data-testid={`gallery-item-${i}`}
              className={`group relative overflow-hidden ${i === 0 ? "col-span-2 row-span-2" : ""}`}
            >
              <img
                src={g.url}
                alt={g.label}
                className="h-full min-h-[160px] w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                <span className="text-sm font-medium text-white">{g.label}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* UBICACIÓN / MAPA */}
      <Section id="ubicacion" className="py-12">
        <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <Overline>Ubicación</Overline>
            <h2 className="font-serif-display text-4xl font-light tracking-tight sm:text-5xl">
              Encuéntranos
            </h2>
            <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin size={16} strokeWidth={1.5} className="text-primary" />
              {ADDRESS}
            </p>
          </div>
          <a
            href={MAP_DIRECTIONS}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="directions-btn"
            className="group flex items-center gap-2 bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark"
          >
            <Navigation size={16} strokeWidth={1.5} />
            Cómo llegar
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </a>
        </div>
        <div className="overflow-hidden border border-border">
          <iframe
            title="Ubicación Estética Lupita"
            data-testid="location-map"
            src={MAP_EMBED}
            width="100%"
            height="420"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section id="opiniones" className="py-24">
        <Overline>Opiniones</Overline>
        <h2 className="mb-10 font-serif-display text-4xl font-light tracking-tight sm:text-5xl">
          Lo que dicen de nosotros
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              custom={i}
              className="border border-border bg-card p-6 transition-colors hover:border-primary"
            >
              <div className="mb-3 flex gap-0.5 text-primary">
                {Array.from({ length: t.rating }).map((_, k) => (
                  <Star key={`${t.id}-star-${k}`} size={15} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <p className="mb-4 text-sm text-muted-foreground">"{t.text}"</p>
              <p className="font-serif-display text-lg">{t.name}</p>
              <p className="overline text-primary">{t.service}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CONTACT / CTA */}
      <Section id="contacto" className="py-24">
        <div className="grid grid-cols-1 overflow-hidden border border-border lg:grid-cols-2">
          <div className="bg-foreground p-10 text-background sm:p-14">
            <Overline>Contacto</Overline>
            <h2 className="font-serif-display text-4xl font-light tracking-tight">
              Visítanos
            </h2>
            <div className="mt-8 space-y-5 text-sm">
              <p className="flex items-start gap-3">
                <MapPin size={18} strokeWidth={1.5} className="mt-0.5 text-primary" />
                Avenida 15 de mayo 224, Puebla, Puebla
              </p>
              <p className="flex items-center gap-3">
                <Phone size={18} strokeWidth={1.5} className="text-primary" />
                +52 55 1234 5678
              </p>
              <p className="flex items-center gap-3">
                <Clock size={18} strokeWidth={1.5} className="text-primary" />
                Lun – Sáb · 9:00 a 19:00
              </p>
            </div>
            <Link
              to="/reservar"
              data-testid="contact-book-btn"
              className="mt-10 inline-flex items-center gap-2 bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark"
            >
              Agenda tu cita <Scissors size={16} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="min-h-[300px]">
            <img
              src="https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6"
              alt="Interior Lupita"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-border py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 sm:flex-row sm:px-8">
          <p className="font-serif-display text-xl">
            Lupita <span className="overline text-primary">Estética</span>
          </p>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Estética Unisex Lupita. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
